
import config from '../config/config';
import { authService } from './authService';
import { normalizeInternalConversationNewMessage } from './internalChatService';

class UserWebSocketService {
  constructor() {
    this.socket = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 30000; // 30 seconds
    this.heartbeatInterval = null;
    this.heartbeatTimeout = 60000; // 60 seconds
    this.isConnecting = false;
    this.isReconnecting = false;
    this.listeners = new Map();
    this.messageQueue = [];
    
    // Store connection parameters for reconnection
    this.currentUserId = null;
  }

  // Connect to User WebSocket
  async connect(userId) {
    if (this.isConnecting || (this.socket && this.socket.readyState === WebSocket.OPEN)) {
      return;
    }

    // Store connection parameters for reconnection
    this.currentUserId = userId;

    this.isConnecting = true;

    if (!userId) {
      console.error('❌ No user ID provided for User WebSocket connection');
      this.isConnecting = false;
      return;
    }

    try {
      // Get and validate token before connecting
      const token = await this.getValidToken();
      if (!token) {
        console.error('❌ No valid access token available for User WebSocket connection');
        this.isConnecting = false;
        return;
      }

      // Build User WebSocket URL with authentication
      const wsUrl = `${config.API.Websocket_user_Base_URL}${userId}/inbox/?token=${token}`;
            
      this.socket = new WebSocket(wsUrl);
      this.setupEventListeners();
    } catch (error) {
      console.error('❌ User WebSocket connection error:', error);
      this.isConnecting = false;
      this.handleReconnect();
    }
  }

  // Get valid token, refresh if necessary
  async getValidToken() {
    try {
      const currentToken = localStorage.getItem('access_token');
      
      if (!currentToken) {
        console.warn('⚠️ No access token found, attempting to refresh...');
        const refreshResult = await authService.refreshAccessToken();
        if (refreshResult.success) {
          return refreshResult.accessToken;
        } else {
          console.error('❌ Failed to refresh token:', refreshResult.error);
          // If logout is required, don't attempt reconnection
          if (refreshResult.shouldLogout) {
            console.warn('🔐 Logout required - stopping User WebSocket reconnection attempts');
            this.emit('token_refresh_failed', { error: refreshResult.error, shouldLogout: true });
          }
          return null;
        }
      }

      // Verify token is still valid
      const verificationResult = await authService.verifyToken(currentToken);
      if (verificationResult.isValid) {
        return currentToken;
      } else {
        console.warn('⚠️ Access token is invalid, attempting to refresh...');
        const refreshResult = await authService.refreshAccessToken();
        if (refreshResult.success) {
          return refreshResult.accessToken;
        } else {
          console.error('❌ Failed to refresh token:', refreshResult.error);
          // If logout is required, don't attempt reconnection
          if (refreshResult.shouldLogout) {
            console.warn('🔐 Logout required - stopping User WebSocket reconnection attempts');
            this.emit('token_refresh_failed', { error: refreshResult.error, shouldLogout: true });
          }
          return null;
        }
      }
    } catch (error) {
      console.error('❌ Error getting valid token:', error);
      return null;
    }
  }

  // Handle token expiration during WebSocket connection
  async handleTokenExpiration() {
    
    try {
      const refreshResult = await authService.refreshAccessToken();
      if (refreshResult.success) {

        this.disconnect();
        // Small delay to ensure clean disconnection
        setTimeout(() => {
          if (this.currentUserId) {
            this.connect(this.currentUserId);
          }
        }, 1000);
        return true;
      } else {
        console.error('❌ Token refresh failed:', refreshResult.error);
        // If logout is required, don't attempt reconnection - logout will handle cleanup
        if (refreshResult.shouldLogout) {
          console.warn('🔐 Logout required - stopping User WebSocket reconnection attempts');
          this.emit('token_refresh_failed', { error: refreshResult.error, shouldLogout: true });
          // Disconnect cleanly since logout is in progress
          this.disconnect();
          return false;
        }
        this.emit('token_refresh_failed', { error: refreshResult.error, retryable: refreshResult.retryable });
        return false;
      }
    } catch (error) {
      console.error('❌ Error during token refresh:', error);
      this.emit('token_refresh_failed', { error: error.message });
      return false;
    }
  }

  // Setup WebSocket event listeners
  setupEventListeners() {
    if (!this.socket) return;

    this.socket.onopen = (event) => {
      this.isConnecting = false;
      this.isReconnecting = false;
      this.reconnectAttempts = 0;
      this.startHeartbeat();
      this.processMessageQueue();
      this.emit('connected', event);
    };

    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        //console.log('📨 User WebSocket message received:', data);
        this.handleMessage(data);
      } catch (error) {
        console.error('❌ Error parsing User WebSocket message:', error);
      }
    };

    this.socket.onclose = async (event) => {
      //console.log('❌ User WebSocket connection closed:', event.code, event.reason);
      this.isConnecting = false;
      this.stopHeartbeat();
      
      // Check if closure was due to authentication failure (401)
      if (event.code === 1008 || event.code === 1002 || event.reason?.includes('401') || event.reason?.includes('unauthorized')) {
        //console.log('🔑 User WebSocket closed due to authentication failure, attempting token refresh...');
        const refreshSuccess = await this.handleTokenExpiration();
        if (refreshSuccess) {
          return; // Token refresh will trigger reconnection
        }
      }
      
      // Only attempt reconnection if it wasn't a manual close
      if (event.code !== 1000) {
        this.isReconnecting = true;
        this.emit('connecting', { reason: 'reconnecting' });
        this.handleReconnect();
      } else {
        this.isReconnecting = false;
        this.emit('disconnected', event);
      }
    };

    this.socket.onerror = (error) => {
      this.isConnecting = false;
      
      if (!this.isReconnecting) {
        this.emit('error', error);
      } else {
        //console.log('🔄 Error during reconnection - not emitting error event to avoid status change');
      }
    };
  }

  // Handle incoming user-level messages
  handleMessage(data) {
    //console.log('📨 Raw User WebSocket message received:', data);
    
    // Handle pong responses to ping messages
    if (data.type === 'pong' || data.event === 'pong') {
      if (this.lastPingTimeout) {
        clearTimeout(this.lastPingTimeout);
        this.lastPingTimeout = null;
      }
      return;
    }

    // Same envelope as tenant WS: { type: "Internal.Notification", payload: { event, conversation } }
    const notifType = (data?.type ?? '').toString();
    const inner = data.payload && typeof data.payload === 'object' ? data.payload : null;
    const innerEvent = inner?.event != null ? String(inner.event) : '';
    const unwrapInternalFavorite =
      innerEvent === 'internal.conversation.favorite.add' ||
      innerEvent === 'internal.conversation.favorite.remove';
    if (
      notifType.toLowerCase() === 'internal.notification' &&
      inner &&
      inner.event &&
      (inner.conversation || inner.message || inner.conversation_id != null || unwrapInternalFavorite)
    ) {
      this.handleMessage({ ...inner });
      return;
    }
    
    // Handle different message formats
    let type, payload;
    
    if (data.event === 'invite.new' && data.participant) {
      type = 'invite.new';
      payload = data;
    } else if (data.event && data.data) {
      type = data.event;
      payload = data.data;
    } else if (data.event && data.conversation) {
      type = data.event;
      payload = data;

      const getName = data.event.split('.')[0] + '.' + data.event.split('.')[1];

      if ( getName === 'internal.conversation' ) {
        payload = data.conversation;
      }
      
    } else if (data.event) {
      
      // Some backend events come as top-level payload:
      // { event: "user.role.changed", ...fields }
      
      type = data.event;
      payload = data;
      

    } else if (data.type && data.payload) {
      type = data.type;
      payload = data.payload;
    } else {
      type = data.type || 'unknown';
      payload = data;
    }
        
    switch (type) {
      case 'user_notification':
        this.emit('user_notification', payload);
        break;
      case 'user_alert':
        this.emit('user_alert', payload);
        break;
      case 'user_status_update':
        this.emit('user_status_update', payload);
        break;
      case 'user_permission_update':
        this.emit('user_permission_update', payload);
        break;
      case 'user_profile_update':
        this.emit('user_profile_update', payload);
        break;
      case 'user_assignment_update':
        this.emit('user_assignment_update', payload);
        break;
      case 'user_online_status':
        this.emit('user_online_status', payload);
        break;
      case 'user_activity_update':
        this.emit('user_activity_update', payload);
        break;
      case 'conversation.assigned':
        this.emit('conversation.assigned', payload);
        break;
      case 'assignment.pending':
        this.emit('assignment.pending', payload);
        break;
      case 'invite.pending':
        this.emit('invite.pending', payload);
        break;
      case 'invite.new':
        this.emit('invite.new', payload);
        break;
      case 'invite.accept':
        this.emit('invite.accept', payload);
        break;
      case 'invite.reject':
      case 'invite. Reject':
        this.emit('invite.reject', payload);
        break;
      case 'message':
      case 'new_message':
        this.emit('message', payload);
        break;
      case 'message.status.updated':
        this.emit('message_status_updated', payload);
        break;
      case 'conversation.updated':

        const status = payload?.status;
        const owner = payload?.owner;
        const currentUserId = this.userId;
        
        // Check if this is a user-scope event
        const inboxStatuses = ['PENDING_INVITE', 'PENDING_TRANSFER', 'Pending Transfer', 'Pending Invite', 'pending_transfer', 'pending_invite'];
        const isMyTab = owner?.id === currentUserId || owner === currentUserId;
        const isUserScopeEvent = inboxStatuses.includes(status) || isMyTab;
        
        if (isUserScopeEvent) {
          //console.log('🔄 Emitting conversation.updated event (user scope)');
          this.emit('conversation.updated', payload);
        } else {
          //console.log('⏭️ Skipping conversation.updated event (tenant scope - handled by tenant websocket)');
        }
        break;
      case 'conversation.accepted':
        break;
      case 'transfer.accept':
        console.log('👤 USER WEBSOCKET: Processing transfer.accept event:', payload);
        this.emit('transfer.accept', payload);
        break;
      case 'transfer.reject':
        console.log('👤 USER WEBSOCKET: Processing transfer.reject event:', payload);
        console.log('👤 USER WEBSOCKET: Emitting transfer.reject event to listeners');
        this.emit('transfer.reject', payload);
        break;
      case 'chat_transferred':
        console.log('👤 USER WEBSOCKET: Processing chat_transferred event:', payload);
        console.log('👤 USER WEBSOCKET: Emitting chat_transferred event to listeners');
        this.emit('chat_transferred', payload);
        break;
      case 'user.role.changed':
      case 'role_update':
        this.emit('user.role.changed', payload);
        break;
      case 'conversation.removed':
        this.emit('conversation.removed', payload);
        break;
      case 'conversation.no_reply_resolve_prompt':
        // Inactive Chat Reminder — show the global reminder modal.
        this.emit('conversation.no_reply_resolve_prompt', payload);
        break;
      case 'conversation.no_reply_resolve_prompt_dismissed':
        // Prompt was dismissed/expired on the backend — hide the reminder modal.
        this.emit('conversation.no_reply_resolve_prompt_dismissed', payload);
        break;
      case 'internal.conversation.group.add':
        this.emit('internal_conversation_group_add', payload);
        break;
      case 'internal.conversation.direct.add':
        this.emit('internal_conversation_direct_add', payload);
        break;
      case 'internal.conversation.add':
        this.emit('internal_conversation_add', payload);
        break;
      case 'internal.conversation.new.message':
        this.emit('tab_indicator_internal_conversation_new_message', payload);
        // break;
      case 'internal.conversation.new.message': {
        const normalized = normalizeInternalConversationNewMessage(
          data,
          data.event && data.conversation ? payload : null
        );
        if (normalized) {
          this.emit('internal_conversation_new_message', normalized);
        }
        break;
      }
      case 'internal.conversation.group.subject.changed':
        this.emit('internal_conversation_group_subject_changed', payload);
        break;
      case 'internal.conversation.group.remove':
        this.emit('internal_conversation_group_remove', payload);
        break;
      case 'internal.conversation.group.owner.transfer':
        this.emit('internal_conversation_group_owner_transfer', payload);
        break;
      case 'internal.conversation.favorite.add':
        this.emit('internal_conversation_favorite_add', payload);
        break;
      case 'internal.conversation.favorite.remove':
        this.emit('internal_conversation_favorite_remove', payload);
        break;
      case 'pong':
        // Heartbeat response
        //console.log('💓 Heartbeat response received');
        break;
      default:
        //console.log('❓ Unknown user message type:', type, 'Data:', data);
        this.emit('unknown', data);
    }
  }

  // Send user-level message through WebSocket
  sendMessage(messageData) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const message = {
        type: 'user_message',
        payload: messageData
      };
      
      // Sending User WebSocket message
      this.socket.send(JSON.stringify(message));
    } else {
      console.warn('⚠️ User WebSocket not connected, queuing message');
      this.messageQueue.push(messageData);
    }
  }

  // Send user status update
  sendUserStatusUpdate(status, metadata = {}) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const message = {
        type: 'user_status_update',
        payload: {
          user_id: this.currentUserId,
          status: status,
          metadata: metadata,
          timestamp: new Date().toISOString()
        }
      };
      
      // Sending user status update
      this.socket.send(JSON.stringify(message));
    }
  }

  // Send user activity update
  sendUserActivityUpdate(activity, details = {}) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const message = {
        type: 'user_activity_update',
        payload: {
          user_id: this.currentUserId,
          activity: activity,
          details: details,
          timestamp: new Date().toISOString()
        }
      };
      
      // Sending user activity update
      this.socket.send(JSON.stringify(message));
    }
  }

  // Start heartbeat to keep connection alive and detect stale connections
  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        // Send ping and set up response timeout
        const pingId = Date.now();
        this.socket.send(JSON.stringify({ type: 'ping', ts: pingId }));
        
        // Set up response timeout - if no pong received within 30 seconds, consider connection stale
        const responseTimeout = setTimeout(() => {
          this.handleStaleConnection();
        }, 30000);
        
        // Store timeout for cleanup when pong is received
        this.lastPingTimeout = responseTimeout;
      } else {
        console.warn('⚠️ User WebSocket not in OPEN state, cannot send ping. State:', this.socket?.readyState);
      }
    }, this.heartbeatTimeout);
  }

  // Stop heartbeat
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.lastPingTimeout) {
      clearTimeout(this.lastPingTimeout);
      this.lastPingTimeout = null;
    }
  }

  // Handle stale connection detection
  handleStaleConnection() {
    console.warn('🔄 Detected stale WebSocket connection, forcing reconnection...');
    this.isReconnecting = true;
    this.emit('connecting', { reason: 'stale_connection' });
    
    // Force close the current connection
    if (this.socket) {
      this.socket.close(1000, 'Stale connection detected');
    }
    
    // Attempt reconnection
    this.handleReconnect();
  }

  // Process queued messages
  processMessageQueue() {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this.sendMessage(message);
    }
  }

  // Handle reconnection
  async handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Max reconnection attempts reached for User WebSocket - stopping reconnection attempts');
      this.isReconnecting = false;
      this.emit('disconnected', { reason: 'max_attempts_reached' });
      this.emit('max_reconnect_attempts_reached');
      return;
    }

    // Check if we have stored connection parameters
    if (!this.currentUserId) {
      console.error('❌ Cannot reconnect User WebSocket: missing user ID');
      this.isReconnecting = false;
      this.emit('disconnected', { reason: 'missing_parameters' });
      this.emit('max_reconnect_attempts_reached');
      return;
    }

    this.reconnectAttempts++;
    
    if (this.reconnectAttempts === 1) {
      this.emit('connecting', { 
        reason: 'reconnecting', 
        attempt: this.reconnectAttempts, 
        maxAttempts: this.maxReconnectAttempts 
      });
    }
    
    // Exponential backoff: 3s, 6s, 12s, 24s, 30s (max)
    const backoffDelay = Math.min(this.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1), 30000);
    
    setTimeout(async () => {
      if (this.socket && this.socket.readyState === WebSocket.CLOSED) {
        try {
          const token = await this.getValidToken();
          if (token) {
            await this.connect(this.currentUserId);
          } else {
            // Check if we should stop reconnection attempts (logout in progress)
            const refreshToken = localStorage.getItem('refresh_token');
            if (!refreshToken) {
              // No refresh token means logout is in progress - stop reconnection attempts
              console.warn('🔐 No refresh token available - logout in progress, stopping User WebSocket reconnection attempts');
              this.isReconnecting = false;
              this.emit('disconnected', { reason: 'logout_in_progress' });
              return;
            }
            console.error('❌ Cannot reconnect User WebSocket: no valid token available');
            this.emit('token_refresh_failed', { error: 'No valid token available for reconnection' });
          }
        } catch (error) {
          console.error('❌ Error during User WebSocket reconnection:', error);
          this.handleReconnect();
        }
      }
    }, backoffDelay);
  }

  // Add event listener
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  // Remove event listener
  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  // Emit event to listeners
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('❌ Error in User WebSocket event listener:', error);
        }
      });
    }
  }

  // Disconnect User WebSocket
  disconnect() {
    this.stopHeartbeat();
    
    if (this.socket) {
      this.socket.close(1000, 'Manual disconnect');
      this.socket = null;
    }
    
    this.isConnecting = false;
    this.isReconnecting = false;
    this.reconnectAttempts = 0;
    this.messageQueue = [];
    
    // Clear stored connection parameters
    this.currentUserId = null;
  }

  // Get connection status
  getConnectionStatus() {
    if (!this.socket) return 'disconnected';
    
    switch (this.socket.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'disconnected';
      default:
        return 'unknown';
    }
  }

  // Check if connected
  isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  // Update connection parameters (useful when switching users)
  updateConnectionParams(userId) {
    
    // If we're currently connected to a different user, disconnect first
    if (this.isConnected() && this.currentUserId !== userId) {
      this.disconnect();
    }
    
    // Store new parameters
    this.currentUserId = userId;
  }

  // Manual reconnect method
  async manualReconnect() {
    
    this.reconnectAttempts = 0;
    this.isReconnecting = true;
    
    this.emit('connecting', { reason: 'manual_reconnect' });
    
    if (this.socket) {
      this.socket.close();
    }
    
    await this.handleReconnect();
  }
}

// Create singleton instance
const userWebSocketService = new UserWebSocketService();

export default userWebSocketService;
